import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { Loader2, Save } from "lucide-react";
import { Controller, useForm } from "react-hook-form";

import type { WikiPage } from "@/api/db/wiki";
import { WikiSchemas } from "@/api/router/wiki/schemas";
import { UnsavedGuard } from "@/components/shared/form/unsaved-guard";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { useWikiEdit } from "@/hooks/use-wiki";
import { WikiLabels } from "@/lib/labels/wiki";

import { WikiEditor } from "./wiki-editor";

export function WikiEdit({ page, onClose }: { page: WikiPage; onClose: () => void }) {
	const [confirmingClose, setConfirmingClose] = useState(false);

	const { mutate, isPending } = useWikiEdit();
	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid },
	} = useForm<{ content: string }>({
		resolver: arktypeResolver(WikiSchemas.edit.pick("content"), undefined, { raw: true }),
		mode: "onChange",
		defaultValues: { content: page.content },
	});

	const submit = handleSubmit(({ content }) => {
		mutate({ slug: page.slug, content }, { onSuccess: onClose });
	});

	function requestClose() {
		if (isDirty && !isPending) {
			setConfirmingClose(true);

			return;
		}

		onClose();
	}

	return (
		<Modal
			title={WikiLabels.edit}
			description={page.title}
			onClose={requestClose}
			className="max-w-2xl"
			slot="wiki-edit-modal"
			isPending={isPending}
		>
			<form onSubmit={submit} className="flex flex-col gap-3">
				<Controller
					control={control}
					name="content"
					render={({ field }) => (
						<WikiEditor
							value={field.value}
							onChange={field.onChange}
							disabled={isPending}
							autoFocus
							onKeyDown={(event) => {
								if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
									event.preventDefault();
									void submit();
								}
							}}
						/>
					)}
				/>

				<div className="mt-1 flex items-center justify-end gap-2">
					<Button
						size="sm"
						variant="secondary"
						type="button"
						disabled={isPending}
						onClick={requestClose}
					>
						{WikiLabels.cancel}
					</Button>

					<Button
						size="sm"
						type="submit"
						variant="default"
						className="px-4"
						disabled={!isDirty || !isValid || isPending}
					>
						{isPending && <Loader2 className="animate-spin" />}
						{!isPending && <Save />}
						{WikiLabels.save}
					</Button>
				</div>
			</form>

			{confirmingClose && (
				<UnsavedGuard onStay={() => setConfirmingClose(false)} onDiscard={onClose} />
			)}
		</Modal>
	);
}
